import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
  Chip,
  Link,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { waitlistAPI } from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import { formatUtcDateTime } from '../utils/dateFormat';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

const STATUS_COLORS = {
  new: 'success',
  contacted: 'info',
  closed: 'default',
};

const STATUSES = ['new', 'contacted', 'closed'];

// Admin and manager only — the ManagerRoute wrapper in App.js keeps everyone
// else out, and every route in backend/routes/waitlist.py is @manager_required.
// So this page assumes the viewer may edit; it has no read-only mode.
const WaitlistPage = () => {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [filters, setFilters] = useState({ status: '' });

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: rowsPerPage, offset: page * rowsPerPage };
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      const res = await waitlistAPI.getAll(params);
      setEntries(res.data.items || []);
      setTotal(res.data.total || 0);
      setError('');
    } catch (err) {
      console.error('Error loading waitlist:', err);
      setError('Could not load the waitlist.');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    loadEntries();
  }, [loadEntries]);

  const handleStatusChange = async (entry, status) => {
    try {
      await waitlistAPI.updateStatus(entry.id, status);
      loadEntries();
    } catch (err) {
      console.error('Error updating waitlist entry:', err);
      setError('Could not update that entry.');
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    try {
      await waitlistAPI.delete(pendingDelete.id);
      setPendingDelete(null);
      loadEntries();
    } catch (err) {
      console.error('Error deleting waitlist entry:', err);
      setError('Could not delete that entry.');
      setPendingDelete(null);
    }
  };

  const foalLabel = (entry) => {
    if (!entry.breeding_record_id) return 'Any foal';
    const mare = entry.mare_name || 'Unknown mare';
    const stallion = entry.stallion_name || 'Unknown stallion';
    return `${mare} × ${stallion}`;
  };

  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
        <Typography variant="h4" sx={{ mb: 2 }}>Foal Waitlist</Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 1 }}>
          <Autocomplete
            size="small"
            disableClearable
            sx={{ minWidth: 160 }}
            options={['', ...STATUSES]}
            getOptionLabel={(s) => s || 'All'}
            filterOptions={createPrefixFilter((s) => s || 'All')}
            value={filters.status}
            onChange={(e, newValue) => {
              setPage(0);
              setFilters((prev) => ({ ...prev, status: newValue || '' }));
            }}
            renderInput={(params) => <TextField {...params} label="Status" />}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
      </Box>

      <Box sx={tablePageScrollRegionSx}>
        <TableContainer component={Paper} sx={tablePageTableContainerSx}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Received</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Interested in</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && <TableSkeletonRows columns={7} />}
              {entries.map((entry) => (
                <TableRow key={entry.id} hover>
                  <TableCell>{formatUtcDateTime(entry.created_at)}</TableCell>
                  <TableCell>{entry.name}</TableCell>
                  <TableCell>
                    <Link href={`mailto:${entry.email}`}>{entry.email}</Link>
                  </TableCell>
                  <TableCell>
                    {entry.phone ? <Link href={`tel:${entry.phone}`}>{entry.phone}</Link> : '—'}
                  </TableCell>
                  <TableCell>{foalLabel(entry)}</TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      variant="standard"
                      value={entry.status}
                      onChange={(e) => handleStatusChange(entry, e.target.value)}
                      renderValue={(value) => (
                        <Chip label={value} size="small" color={STATUS_COLORS[value] || 'default'} />
                      )}
                    >
                      {STATUSES.map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => setPendingDelete(entry)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && entries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">No waitlist signups yet.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100, 200]}
      />

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete waitlist entry?"
        message={
          pendingDelete
            ? `Permanently delete the signup from ${pendingDelete.name} (${pendingDelete.email})? This cannot be undone.`
            : ''
        }
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </Box>
  );
};

export default WaitlistPage;
